import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { Water } from 'three/examples/jsm/objects/Water.js'
import { NIGHT_MODE, SUN_KEY_COLOR_NIGHT, SHADOWS } from './sceneLights'
// Blender 侧算好的树位清单（art/build_roads.py 写出）。缺文件时 webpack 直接报错，
// 所以生成脚本和这份资产要一起提交；运行时仍会校验 spots 非空才启用。
import TREE_PLACES from '@/assets/environment/tree-places.json'
// 成林带树位清单（art/build_forest_bands.py 写出）：以前是烘在 landscape.glb 里的
// ForestBroad / ForestFlower 两团网格，干冠配不上对还白占 17 万顶点，现在并进下面的
// 树位清单，和园区的树用同一套原型实例化。
import FOREST_BANDS from '@/assets/environment/forest-band-spots.json'
// 贴花清单：树影 / 楼影 / 光池，三条都是 [东, 北, 标高, 半径]
import DECAL_PLACES from '@/assets/environment/decal-places.json'

/**
 * 简化版环境元素：水体（河带 / 池塘）+ 植被（低模树 / 灌丛 / 草坪）。
 *
 * 资产来源（双轨制）：
 *   1. 首选 Blender 建模导出的 GLB（src/assets/environment/）：
 *      · river.glb —— 河带网格（CatmullRom 中心线 + 宽度微变 + 岸线噪声），
 *        在 Blender 里按「场景原点 = 项目模型锚点、X=东 / Y=北 / Z=上」绝对坐标制作，
 *        加载后包一层 rotX(90°)（与城市模型同款 Y-up -> Z-up 处理）即可对位；
 *      · trees.glb —— 各树种的**原型**几何（TreeBroad / TreePine / TreeFlower /
 *        TreeRound / Bush 的干与冠），只取几何与材质做实例化（InstancedMesh）；
 *      · tree-places.json —— 树位清单，由 art/build_roads.py 按 OSM 真实路网/楼体/水体
 *        筛位后写出（[东, 北, 地形标高, 树种索引, 缩放, 绕Z旋转]）。位置在 Blender 侧定，
 *        本模块不再自己撒树 —— 之前烘 4700 棵树顶点进 roads.glb 是 30MB，现在 3.3MB。
 *      · forest-band-spots.json —— 成林带树位，art/build_forest_bands.py 从 Blender 里的
 *        林带对象反读，格式同上、树种索引按自己的 species 表换算后并进 tree-places 一起铺。
 *   2. GLB 加载失败自动回退程序化几何（旧方案，纯代码生成），保证页面永远能出图。
 *
 * 定位基准（与 sceneView 的世界坐标一致，Z-up 铁律）：
 *   X = 东 / Y = 北 / Z = 上，单位米；原点 = 三维场景原点（模型锚点钉住的点）。
 *
 * 设计取舍：
 *   - 树的摆放 = 「沿每栋有名称的楼周边自动一圈 + 手动簇补充」，自动避开楼体
 *     占地与水面；随机数用固定种子，每次刷新布局完全一致，方便对照调参。
 *   - 水面**不做顶点波浪动画**：本场景按需渲染（相机不动不画），持续动画会把
 *     帧率永远拉满，与「拖动发黏」的优化初衷冲突。
 */

// ============ 总开关 ============
export const ENV_ENABLED = true

// ============ 道路 / 广场铺装 ============
/**
 * roads.glb 由 Blender 侧脚本生成（art/build_roads.py，走 blender-mcp socket 跑）。
 * 路面标高已按 Terrain 实测起伏烘进顶点，所以这里**不要再给 z 偏移**，
 * 否则整层会浮起来或埋进地里。
 */
export const ROADS_ENABLED = true
/** 整组平移（米）：线位画偏了先在这里精调，不必回 Blender 重导出 */
export const ROADS_GLB_OFFSET = { east: 0, north: 0 }

// ============ 周边城市背景（Blender: art/build_context.py 由 OSM 真实轮廓生成）============
/**
 * context.glb = 园区外围 2.4km 内的 1800+ 栋建筑体块（OSM 真实底面 + 按类型给高度）。
 * 之所以自建：高德 v2 的 AMap.Buildings 只是楼块样式图层，本项目这套 GLCustomLayer
 * 接法下底图压根不渲染 3D 楼块（showBuildingBlock / zooms / 配色全试过，无效）。
 *
 * 空气透视**不用额外做**：这些是我们的 three 物体，scene.fog（NIGHT_FOG）会自动
 * 把它们按距离沉进夜色 —— 高德楼块拿不到的就是这个效果。
 */
export const CONTEXT_ENABLED = true
/** 整组平移（米）：OSM 与模型的对齐实测偏差在几十米内，需要微调时改这里 */
export const CONTEXT_GLB_OFFSET = { east: 0, north: 0 }

// ============ 地台边缘 ============
/**
 * 城市铺装盘（landscape.glb 里 UrbanPaving 那整盘石板地，半径 975m）按到园区中心的
 * 水平距离渐隐。
 *
 * 2026-10-21 之前渐隐挂在地台（LMatGround 绿色方台）上、铺装只是贴在草地上的薄片：
 * 远视距深度精度分辨不出 8cm 高差，绿草和石板互穿成绿斑，薄片也读起来像纸。
 * 现在 Terrain 不再进 GLB，盘内只有一层地面，渐隐改挂铺装材质 —— 盘子边缘化进
 * 高德底图，过渡自然，也不用把盘子做大。
 */
export const GROUND_EDGE_FADE = true
/**
 * 渐隐区间必须和 art/build_context.py 的 CAMPUS_KEEP 配套：
 * 草地先化掉，城市体块再从同一带里立起来。反过来（草地不透明到很晚、城市早就开始）
 * 会让楼站在纯绿草坪上，非常假。
 */
/**
 * ⚠️ 渐隐圆心是**园区中心**，不是世界原点。
 * 之前按原点算：圆心差 (101,318) = 334m，而 art/build_roads.py 的树/路是按园区中心
 * 半径 880m 铺的 —— 结果实测 15473 棵树里 7169 棵落在渐隐带上、2219 棵干脆落在
 * 已经完全 discard 的地面上（截图右下角那些"飘在天上的树"）。
 * 半径也不能随便给大：地台是 ±1300m 的正方形，从园区中心往北只剩 982m，
 * 外圈超出正方形就会露出一条 50% 透明度的直角硬边。
 */
export const GROUND_FADE_CENTER = { x: 101, y: 318 }
export const GROUND_FADE_INNER = 860
export const GROUND_FADE_OUTER = 975

/** 哪些材质被打了边缘渐隐：必须用 WeakSet，**不能挂 mat.userData** ——
 *  GLTFLoader 给每个网格 clone 材质，Material.copy 会连 onBeforeCompile 一起拷，
 *  后拷的"未打标记"副本会顶掉先拷的补丁版；而 userData 里那个标记若被拷进这些副本，
 *  它们又不在本模块遍历范围内、uniform 永远没人设 → 0/0 让 alpha 恒 0，
 *  草床整块透明，露出高德底图白色圆形绿地（2026-10-21 "白饼"根因）。 */
const groundFadeMats = new WeakSet()

/** 地台边缘渐隐的材质补丁：按世界系水平半径压 alpha，边缘外整段 discard 掉。
 *  ⚠️ 这个补丁会把地台变成 transparent 材质，于是它进了透明队列、按质心距离排序 ——
 *  水面（glTF BLEND，GLTFLoader 会顺手 depthWrite=false）挡不住它，就会被草地糊住。
 *  所以 WATER_RENDER_ORDER 必须高于地台，见下面水面的处理。 */
function applyGroundEdgeFade(mat) {
  mat.transparent = true
  groundFadeMats.add(mat)
  mat.customProgramCacheKey = () => 'ground-edge-fade'
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.groundFadeInner = { value: GROUND_FADE_INNER }
    shader.uniforms.groundFadeOuter = { value: GROUND_FADE_OUTER }
    shader.uniforms.groundFadeCenter = { value: GROUND_FADE_CENTER }
    shader.vertexShader =
      'varying vec3 vGroundWorld;\n' +
      shader.vertexShader.replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n  vGroundWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      )
    shader.fragmentShader =
      'uniform float groundFadeInner;\nuniform float groundFadeOuter;\nuniform vec2 groundFadeCenter;\nvarying vec3 vGroundWorld;\n' +
      shader.fragmentShader.replace(
        '#include <opaque_fragment>',
        `#include <opaque_fragment>
  {
    // 世界系 Z-up：X=东 / Y=北，水平半径 = 到园区中心的距离（不是到世界原点）
    float r = length(vGroundWorld.xy - groundFadeCenter);
    float f = 1.0 - smoothstep(groundFadeInner, groundFadeOuter, r);
    if (f < 0.02) discard;
    gl_FragColor.a *= f;
  }`,
      )
    // ⚠️ 别把 shader 挂到 mat.userData 上"留着以后调"：Material.copy() 对 userData
    // 走 JSON 深拷，shader 里是两份完整 GLSL，每次 clone 都要序列化几百 KB
    //（同样的坑见 loadCityTiles.js 的 patchFacadeMaterial 注释）。这里没人读它。
  }
  mat.needsUpdate = true
}

// ============ 水体 ============
/** 河带宽度（米）——仅程序化兜底河道使用；GLB 河的宽度在 Blender 里定型 */
/**
 * 程序化兜底河：贴着建筑群南侧往南 RIVER_OFFSET 米，东西各越界 260m，正弦微弯。
 * ⚠️ **仅兜底，且不贴地形**：中心线是手描的、标高恒定 WATER_Z，Terrain 起伏它一概不管。
 * roads.glb 带来真水时这条整条不画（见 createEnvironment 里的 roadsHasWater 门）——
 * 之前两边一起画，画面里就多出一条横贯绿岛的蓝色胶带，被当成"河"查了好几轮。
 * 要改宽度/走向之前先确认你真的在没有 roads.glb 水面的场景里。
 */
export const RIVER_WIDTH = 55
/** 程序化兜底河道：离「建筑群最南一排楼」的距离（米） */
export const RIVER_OFFSET = 150
/** 程序化兜底河道的正弦弯曲幅度（米） */
export const RIVER_AMPLITUDE = 45
/** 水面默认颜色 / 不透明度（半透明：能隐约透出底下底图，更像浅水） */
export const RIVER_COLOR = 0x2f6f9f
export const WATER_OPACITY = 0.85
/**
 * 水面体色（Water 着色器的 waterColor，只进"俯视那一档"的 diffuse 项）和
 * **反射项的蓝调倍率**。后者才是关键：Water 按 Fresnel 把平面反射混进颜色
 * （`rf0 = 0.3`，掠射角几乎全反射），而这一带的水面反的是浅灰铺装 + 灰白背景楼，
 * 不压一下就是"灰镜子"。
 * ⚠️ 色相判据同 dusk-palette：**b−g > g−r 就是紫倾向**。2026-10-22 用户判"河偏紫蓝"，
 * 取样坐实了：vec3(0.30,0.60,1.08) 落地 rgb=(110,131,162)、b−g=+31 > g−r=+21、色相 216°。
 * 第一版改 vec3(0.27,0.72,1.02) → 色相 200.3°（天蓝）、b−g +19.4 < g−r +38.0，判据翻正。
 * 同日再"增强一档蓝"：把 **R 砍到 0.17**（灰掉的反射才是"蓝得不明显"的根因，不是 B 不够高）、
 * B 抬到 1.10 → 彩度 (max−min)/max 0.75 → 0.85，色相只往 205° 靠一点，仍离 216° 的紫很远。
 * ⚠️ 高光项（`reflectionSample * specularLight`）**不乘**：它代表夕照侧那盏主光（见
 * sceneLights 的 SUN_KEY_COLOR_NIGHT），乘上蓝倍率等于给主光换两次色。
 */
export const WATER_COLOR_NIGHT = 0x0f5f8c
export const WATER_REFLECT_TINT = 'vec3( 0.17, 0.72, 1.10 )'
/** 水面海拔（米）——略抬高，防止与光环等贴地元素打架 */
export const WATER_Z = 0.5
/**
 * 水面的 renderOrder。
 * ⚠️ 不是可选的美化：地台为了边缘渐隐被设成 transparent，于是它和水体一起进透明队列，
 * 而队列内部按"质心到相机的距离"排序 —— 地台是一整块 2600m 的大平面、质心就在园区中心，
 * 河带质心往往更远，所以地台后画；水面材质是 glTF BLEND（GLTFLoader 顺手 depthWrite=false），
 * 既写不了深度也挡不住后画的东西，结果就是"河被草地盖住"。renderOrder 优先于距离排序。
 */
export const WATER_RENDER_ORDER = 6
/**
 * GLB 河带的整组平移（米）：{ east, north }。河道形状已在 Blender 里定型
 * （南侧主河道 + 东端向北弯），这里只做精调 —— 觉得河离楼太近/太远就改这里，无需重导出。
 */
export const RIVER_GLB_OFFSET = { east: 0, north: 0 }
/**
 * GLB 河的中心线（[[东, 北], ...]），与 Blender 里 ctrl 点一致，仅用于
 * 树位避水判断（树离河中心线小于避让半径就跳过）。改了 Blender 河道后同步这里。
 */
export const RIVER_CENTERLINE_GLB = [
  [-1150, -360],
  [-750, -450],
  [-250, -480],
  [300, -430],
  [700, -330],
  [950, -160],
  [1080, 60],
  [1130, 260],
]
/** 程序化兜底河道中心线（null = 按建筑群南侧自动生成） */
export const RIVER_POINTS = null
/** 池塘/湖面：[{ points: [[东,北],...], color?, opacity? }]，至少 3 个顶点（程序化兜底） */
export const PONDS = []

// ============ 植被 ============
/** 园区草坪基面：一大片椭圆草地垫在建筑群下面（消除黑底、树有落点才不「漂浮」） */
export const AUTO_GROUND = true
export const GROUND_COLOR = 0x224529
/** 地台顶点色的线性增亮系数（乘在 Terrain 的顶点色上，r/g/b 就是 RGB 三通道）。
 * ⚠️ 别给太高：绿岛是全画面最亮的东西时，它会读成"一块发光的睡莲叶浮在黑城市上"，
 * 而参考图是城包着镇、绿地只是镇子脚下的一片地。1.35/1.55 那版就是太亮了。 */
export const GROUND_TINT = { r: 0.98, g: 1.12, b: 0.9 }
export const GROUND_MARGIN = 260
/** 沿每栋有名称的楼周边自动撒树 */
export const AUTO_TREES = true
/** 每栋楼撒树数量范围 [min, max] —— 对照效果图里「树冠成片」的密度 */
export const TREES_PER_BUILDING = [14, 22]
/** 树整体缩放范围 —— 效果图里的树冠很大，撑得起画面 */
export const TREE_SCALE = [1.6, 2.4]
/**
 * 园区森林填充：在草坪范围内随机撒一片成林的树（避开楼体与水面），
 * 参考效果图里「地面上除了楼就是绿」的效果全靠这片成林。
 */
export const AUTO_FOREST = true
export const AUTO_FOREST_COUNT = 420
/** 树干离楼体占地边缘的最小间距（米） */
export const TREE_MARGIN = 8
/** 树高 / 树冠半径范围（米）——仅程序化兜底树使用 */
export const TREE_HEIGHT = [9, 16]
export const CANOPY_RADIUS = [3.5, 6]
/** 松树（塔形锥冠、墨绿）比例；开花树（粉冠）比例；其余阔叶（多面体球冠） */
export const PINE_RATIO = 0.15
/** 花树压到 12%：效果图里粉色只是点缀，大片粉冠会显得像糖果 */
export const FLOWER_RATIO = 0.12
/** 每棵树旁附带灌丛（贴地小圆球）的概率 —— 灌丛是「填满地面」的主力 */
export const BUSH_RATIO = 0.65
/**
 * 手动树簇：[{ center: [东,北], radius, count }]，圆内均匀散布，
 * 自动避开楼体与水面。默认为空（自动撒树已覆盖），想要林带/广场绿化时再加。
 */
export const TREE_CLUSTERS = []
/** 在建筑群几何中心自动铺一片草坪（圆心被楼压住就自动放弃，宁缺毋滥） */
export const AUTO_LAWNS = true
/**
 * 手动草坪：[{ center: [东,北], radius, color? }]，贴地半透明圆片。
 * 圆心落在楼体里时自动跳过。
 */
export const LAWNS = []

// ============ 贴花：接触阴影 + 灯光池 ============
/**
 * 参考图里每棵树都"压"在地上，我们之前全是贴上去的 —— 因为没有任何东西把它们和
 * 地面连起来。这里用一张软边径向渐变的四边形 InstancedMesh 补两种：
 *   · 接触阴影：每棵大树底下一块（往西南偏，主光 rimNE 从东北来）；
 *   · 光池：路灯正下方一圈暖光，Additive 叠在地面上。
 * 位置由 art/build_roads.py 写出的 decal-places.json 给。
 * ⚠️ 这是"假阴影"：哪天开了真 shadow map，必须把这里关掉，否则地面会叠两层黑。
 * ⚠️ 楼底阴影和"楼前铺装"已撤：占地只有对角半径一个数，画出来是冲出楼体的圆饼，
 *    个别楼锚点又和可见模型对不上，草地上会留下没有楼的盘子（用户圈图指出）。
 */
export const GROUND_DECALS = true
/** 贴花抬离地面的高度（米）：必须高于最高的路面构造（缘石顶 ≈ +0.34），否则会被路埋掉 */
export const DECAL_LIFT = 0.42
export const SHADOW_OPACITY = 0.45
/** 树影相对冠幅往西南漂移的比例（0 = 正下方一圈，纯装饰性的"接地"） */
export const SHADOW_TREE_DRIFT = 0.3
/** 灯下光池：走**普通混合**，只做"暖光落在地面上"的一层染色。
 *  以前是 additive，目的就是往底色上叠亮度、把像素推过 Bloom 阈值 —— 但 Bloom 比的
 *  是**线性值**（composer 那条路上材质一律不做色调映射，ACES 是 OutputPass 最后一次性
 *  做的），而这一圈光池压着的恰好是被 4.0 强度暖光照到的白楼立面，线性值本来就 >1，
 *  再加一层就直接炸成 200×120px 的白坑（半径从 9m 砍到 5m、不透明度砍到 0.22 都还在炸）。
 *  结论：大面积元素不能拿来做 additive 发光体，发光交给小亮点（LAMP_HDR_STRENGTH）。 */
export const LIGHT_POOL_OPACITY = 0.34
export const LIGHT_POOL_COLOR = 0xffb877
/** 灯头的自发光强度：1m 见方的小亮点，线性值过阈值就是星芒。
 *  6.0（Blender 里那版）会把半条街糊成一片，1.5 是"亮得起又不连面"的位置。 */
export const LAMP_HDR_STRENGTH = 1.5

/** 冠层配色（仅程序化兜底树使用）：阔叶绿 / 开花粉，渲染时小幅 HSL 抖动避免「复印」感 */
const GREENS = [0x3f7d52, 0x4c8f5e, 0x5da06b, 0x6fae74]
const PINKS = [0xeaa8c4, 0xf2bcd0, 0xe393b4]

/** 随机种子：固定值保证每次刷新树的布局一致 */
const RNG_SEED = 20260930

// ============ GLB 资产 URL（webpack require.context，与 loadCityTiles 同款） ============
const glbCtx = require.context('@/assets/environment', true, /\.glb$/)
const GLB_URLS = {}
glbCtx.keys().forEach((key) => {
  GLB_URLS[key.replace('./', '')] = glbCtx(key) // 'trees.glb' -> 打包后的 URL
})

/** mulberry32：小而快的可复现伪随机数（固定种子 -> 布局稳定） */
function mulberry32(seed) {
  let a = seed >>> 0
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// ============ 程序化兜底树的单位几何（尺寸 1，实际大小靠实例缩放） ============

/** 单位树干：高 1、底面落在 z=0（几何已烘焙 rotX(90°) 转成 Z-up） */
function unitTrunkGeometry() {
  const g = new THREE.CylinderGeometry(0.55, 0.8, 1, 6)
  g.translate(0, 0.5, 0)
  g.rotateX(Math.PI / 2)
  return g
}

/** 单位松冠：锥形，高 1、底在 z=0 */
function unitPineGeometry() {
  const g = new THREE.ConeGeometry(1, 1, 7)
  g.translate(0, 0.5, 0)
  g.rotateX(Math.PI / 2)
  return g
}

/** 单位阔叶冠 / 灌丛：多面体球（低模感），心在原点，靠实例缩放压扁 */
function unitBlobGeometry() {
  return new THREE.IcosahedronGeometry(1, 0)
}

/**
 * 折线中心线 -> 等宽河带网格（XY 平面，Z-up 世界里天然水平朝上）。
 * 每个节点用「前后两段的平均方向」求法向并偏移 ±宽/2 —— 转角处自然斜接。
 */
function ribbonGeometry(pts, width) {
  const n = pts.length
  if (n < 2) return null
  const half = width / 2
  const left = []
  const right = []
  for (let i = 0; i < n; i++) {
    const prev = pts[Math.max(0, i - 1)]
    const next = pts[Math.min(n - 1, i + 1)]
    let dx = next[0] - prev[0]
    let dy = next[1] - prev[1]
    const len = Math.hypot(dx, dy) || 1
    dx /= len
    dy /= len
    const nx = -dy
    const ny = dx
    left.push([pts[i][0] + nx * half, pts[i][1] + ny * half])
    right.push([pts[i][0] - nx * half, pts[i][1] - ny * half])
  }
  const pos = []
  left.forEach((p) => pos.push(p[0], p[1], 0))
  right.forEach((p) => pos.push(p[0], p[1], 0))
  const idx = []
  for (let i = 0; i < n - 1; i++) {
    const a = i * 2
    const b = i * 2 + 1
    const c = (i + 1) * 2
    const d = (i + 1) * 2 + 1
    idx.push(a, c, b, b, c, d)
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  geo.setIndex(idx)
  geo.computeVertexNormals()
  return geo
}

/** 点到线段距离（水面避让用） */
function distToSegment(px, py, a, b) {
  const abx = b[0] - a[0]
  const aby = b[1] - a[1]
  const l2 = abx * abx + aby * aby
  if (!l2) return Math.hypot(px - a[0], py - a[1])
  let t = ((px - a[0]) * abx + (py - a[1]) * aby) / l2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (a[0] + abx * t), py - (a[1] + aby * t))
}

function distToPolyline(px, py, pts) {
  let best = Infinity
  for (let i = 0; i < pts.length - 1; i++) {
    best = Math.min(best, distToSegment(px, py, pts[i], pts[i + 1]))
  }
  return best
}

/** 是否落在某栋楼的占地圆内（pad = 额外留边，米） */
function inAnyBuilding(px, py, buildings, pad) {
  return buildings.some((b) => {
    const r = b.radius + pad
    const de = px - b.east
    const dn = py - b.north
    return de * de + dn * dn < r * r
  })
}

/** 程序化兜底河中心线：贴着建筑群南侧往南 RIVER_OFFSET 米，东西各越界 260m，正弦微弯 */
function defaultRiverCenterline(buildings) {
  if (!buildings.length) return []
  let minN = Infinity
  let minE = Infinity
  let maxE = -Infinity
  buildings.forEach((b) => {
    minN = Math.min(minN, b.north)
    minE = Math.min(minE, b.east)
    maxE = Math.max(maxE, b.east)
  })
  const n0 = minN - RIVER_OFFSET
  const e0 = minE - 260
  const e1 = maxE + 260
  const STEPS = 10
  const pts = []
  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS
    pts.push([e0 + (e1 - e0) * t, n0 + Math.sin(t * Math.PI * 1.6 + 0.6) * RIVER_AMPLITUDE])
  }
  return pts
}

/** 水面材质（程序化兜底用）：低粗糙 + 少许金属感，让平行光在水面拉出高光条 */
function waterMaterial(color, opacity) {
  return new THREE.MeshStandardMaterial({
    color: color ?? RIVER_COLOR,
    transparent: true,
    opacity: opacity ?? WATER_OPACITY,
    roughness: 0.22,
    metalness: 0.35,
    depthWrite: false, // 与白模半透明立面共存，避免透明排序闪面
    side: THREE.DoubleSide,
  })
}

// ============ Three.js Water 着色器水面（参考 icegl threeExampleOcean / three/addons/objects/Water.js） ============
/**
 * 程序化生成水面法线贴图（替代 three.js ocean 示例里的 waternormals.jpg）。
 * 多频率正弦波叠加 → 高度场 → 法线，天然周期性保证 tile 无缝。
 * Water 着色器内部会对这张图做 4 层八度采样（uv0~uv3），所以即使纹理简单也能出丰富涟漪。
 */
let _waterNormalsTex = null
function waterNormalsTexture(size = 256) {
  if (_waterNormalsTex) return _waterNormalsTex
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')
  const img = ctx.createImageData(size, size)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const fx = (x / size) * Math.PI * 2
      const fy = (y / size) * Math.PI * 2
      // 多频率正弦波：低频大波 + 中频 + 高频细节，方向错开避免条纹
      let dx = 0
      let dy = 0
      dx += Math.cos(fx * 4) * 0.50
      dy += Math.sin(fy * 4) * 0.50
      dx += Math.cos(fx * 7 + fy * 3) * 0.30
      dy += Math.sin(fx * 3 + fy * 7) * 0.30
      dx += Math.cos(fx * 13 + fy * 11) * 0.20
      dy += Math.sin(fx * 11 + fy * 13) * 0.20
      // 法线 = normalize(-dH/dx, -dH/dy, 1)，编码到 [0,1] 再 ×255
      const len = Math.sqrt(dx * dx + dy * dy + 1)
      const i = (y * size + x) * 4
      img.data[i] = Math.round((-dx / len * 0.5 + 0.5) * 255)
      img.data[i + 1] = Math.round((-dy / len * 0.5 + 0.5) * 255)
      img.data[i + 2] = Math.round((1 / len * 0.5 + 0.5) * 255)
      img.data[i + 3] = 255
    }
  }
  ctx.putImageData(img, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.NoColorSpace // 法线贴图不做 sRGB 转换
  _waterNormalsTex = tex
  return tex
}

/**
 * 太阳方向（Z-up 世界系）：取主光 rimSW（方位 208°、俯仰 24°）的方向向量。
 * Water 着色器用这个算镜面高光：sunLight(reflect(-sunDir, normal))。
 */
const WATER_SUN_DIR = new THREE.Vector3(
  Math.sin((208 * Math.PI) / 180) * Math.cos((24 * Math.PI) / 180),
  Math.cos((208 * Math.PI) / 180) * Math.cos((24 * Math.PI) / 180),
  Math.sin((24 * Math.PI) / 180),
)

/**
 * 替换 Water 对象的 onBeforeRender，把 local normal 从 (0,0,1) 改成 (0,1,0)，
 * 并把"反射那一遍渲染"收敛成**每帧全场景只画一次**。
 *
 * Water.js 的 onBeforeRender 在闭包里硬编码 normal.set(0,0,1)，这是
 * THREE.PlaneGeometry 的默认朝向（XY 面、法线 +Z）。但 GLB 导出的水面
 * 几何在 Y-up 局部系里是 XZ 面、法线 +Y，所以必须改成 (0,1,0)。
 *
 * 因为拿不到原闭包里的 renderTarget 等局部变量，这里自建一套等价逻辑：
 *   · 反射贴图改用 hub 里那张**共享 RT**（尺寸跟构造参数一致，4 片水面同一张）
 *   · 每帧最多一个水面出图，其余水面只更新自己的 textureMatrix 然后复用这张图
 *     —— 反射渲染的是**整个场景**，roads.glb 有 4 片水面就是 1+4 遍，
 *     实测拖动时一帧 1700 万三角形、10fps（2026-10-16 用 tools/cdp-drag-cost.js 量的）；
 *     而且这一遍本身就要 20ms/帧，所以拖动途中干脆不出图，停下补一张（见 hub.begin）
 *   · 渲染反射那一遍前调用 hub.hideForReflection()：水面自己（共用 RT，读写同一张是
 *     GL 反馈环）、树/贴花/标牌这些在 256² 贴图里一个像素都占不到的东西全部藏掉
 *
 * 4 片水面基本同高（同一条河），共用的镜像视点差别在 256² 的贴图里看不出来。
 */
function patchWaterReflectionForZUp(water, hub) {
  const uniforms = water.material.uniforms
  const texW = uniforms.mirrorSampler.value?.image?.width || 256
  const texH = uniforms.mirrorSampler.value?.image?.height || 256
  if (!hub.target) hub.target = new THREE.WebGLRenderTarget(texW, texH)
  const renderTarget = hub.target
  uniforms.mirrorSampler.value = renderTarget.texture
  hub.reflectionSkip.push(water)

  const mirrorWorldPosition = new THREE.Vector3()
  const cameraWorldPosition = new THREE.Vector3()
  const rotationMatrix = new THREE.Matrix4()
  const normal = new THREE.Vector3()
  const view = new THREE.Vector3()
  const lookAtPosition = new THREE.Vector3()
  const target = new THREE.Vector3()
  const mirrorCamera = new THREE.PerspectiveCamera()
  const textureMatrix = uniforms.textureMatrix.value // 复用 Water 构造时创建的 Matrix4 uniform
  const mirrorPlane = new THREE.Plane()
  const clipPlane = new THREE.Vector4()
  const q = new THREE.Vector4()
  const clipBias = 0.0

  water.onBeforeRender = function (renderer, scene, camera) {
    mirrorWorldPosition.setFromMatrixPosition(water.matrixWorld)
    cameraWorldPosition.setFromMatrixPosition(camera.matrixWorld)

    rotationMatrix.extractRotation(water.matrixWorld)

    // ★ 唯一改动：(0,1,0) 而非 (0,0,1) —— GLB Y-up 几何的 local normal
    normal.set(0, 1, 0)
    normal.applyMatrix4(rotationMatrix)

    view.subVectors(mirrorWorldPosition, cameraWorldPosition)
    if (view.dot(normal) > 0) return
    view.reflect(normal).negate()
    view.add(mirrorWorldPosition)

    rotationMatrix.extractRotation(camera.matrixWorld)
    lookAtPosition.set(0, 0, -1)
    lookAtPosition.applyMatrix4(rotationMatrix)
    lookAtPosition.add(cameraWorldPosition)

    target.subVectors(mirrorWorldPosition, lookAtPosition)
    target.reflect(normal).negate()
    target.add(mirrorWorldPosition)

    mirrorCamera.position.copy(view)
    mirrorCamera.up.set(0, 1, 0)
    mirrorCamera.up.applyMatrix4(rotationMatrix)
    mirrorCamera.up.reflect(normal)
    mirrorCamera.lookAt(target)

    mirrorCamera.far = camera.far
    mirrorCamera.updateMatrixWorld()
    mirrorCamera.projectionMatrix.copy(camera.projectionMatrix)

    textureMatrix.set(0.5, 0.0, 0.0, 0.5, 0.0, 0.5, 0.0, 0.5, 0.0, 0.0, 0.5, 0.5, 0.0, 0.0, 0.0, 1.0)
    textureMatrix.multiply(mirrorCamera.projectionMatrix)
    textureMatrix.multiply(mirrorCamera.matrixWorldInverse)

    mirrorPlane.setFromNormalAndCoplanarPoint(normal, mirrorWorldPosition)
    mirrorPlane.applyMatrix4(mirrorCamera.matrixWorldInverse)

    clipPlane.set(mirrorPlane.normal.x, mirrorPlane.normal.y, mirrorPlane.normal.z, mirrorPlane.constant)

    const projectionMatrix = mirrorCamera.projectionMatrix
    q.x = (Math.sign(clipPlane.x) + projectionMatrix.elements[8]) / projectionMatrix.elements[0]
    q.y = (Math.sign(clipPlane.y) + projectionMatrix.elements[9]) / projectionMatrix.elements[5]
    q.z = -1.0
    q.w = (1.0 + projectionMatrix.elements[10]) / projectionMatrix.elements[14]

    clipPlane.multiplyScalar(2.0 / clipPlane.dot(q))

    projectionMatrix.elements[2] = clipPlane.x
    projectionMatrix.elements[6] = clipPlane.y
    projectionMatrix.elements[10] = clipPlane.z + 1.0 - clipBias
    projectionMatrix.elements[14] = clipPlane.w

    uniforms.eye.value.setFromMatrixPosition(camera.matrixWorld)

    // 这一帧不出反射图（拖动途中、或本帧已经有水面出过）：上面那套镜像矩阵照常算
    //（各水面标高不同，采样矩阵必须是自己那份），只是不再重复渲染整个场景
    if (!hub.reflect || hub.done === hub.token) return

    // Render
    const currentRenderTarget = renderer.getRenderTarget()
    const currentXrEnabled = renderer.xr.enabled
    const currentShadowAutoUpdate = renderer.shadowMap.autoUpdate

    // 先认领本帧这一次：下面 renderer.render 会再次遍历到别的水面，
    // 靠这个标记让它们走上面那条 return，不会递归出第二遍全场景
    hub.done = hub.token
    hub.hideForReflection()
    renderer.xr.enabled = false
    renderer.shadowMap.autoUpdate = false
    renderer.setRenderTarget(renderTarget)
    renderer.state.buffers.depth.setMask(true)
    if (renderer.autoClear === false) renderer.clear()
    renderer.render(scene, mirrorCamera)

    hub.showAfterReflection()
    hub.stale = false // 这张图现在对得上相机了，直到下一次相机动过
    renderer.xr.enabled = currentXrEnabled
    renderer.shadowMap.autoUpdate = currentShadowAutoUpdate
    renderer.setRenderTarget(currentRenderTarget)

    const viewport = camera.viewport
    if (viewport !== undefined) {
      renderer.state.viewport(viewport)
    }
  }
}

/**
 * 把 roads.glb 里的静态水面网格替换成 Three.js Water 着色器对象。
 *
 * Water 着色器（three/addons/objects/Water.js）提供：
 *   · 平面反射 —— onBeforeRender 里自动用镜像相机渲染整个场景到 render target
 *     （多片水面共用 hub 里那一张，每帧只渲一遍，见 patchWaterReflectionForZUp）
 *   · 法线贴图涟漪 —— 4 层八度噪声 + 时间动画
 *   · 太阳镜面高光 —— sunDirection + sunColor 驱动
 *
 * ⚠️ Z-up 适配：Water 着色器是为 Y-up 写的，噪声采样用 worldPosition.xz、
 * 法线用 noise.xzy。本项目世界是 Z-up（X=东 / Y=北 / Z=上），水平面是 XY 而非 XZ，
 * 所以必须改 fragment shader：
 *   worldPosition.xz → worldPosition.xy（噪声 UV：水平坐标）
 *   noise.xzy        → noise.xyz      （法线：Z 分量是"上"）
 *   surfaceNormal.xz → surfaceNormal.xy（反射扰动：水平分量）
 *
 * @param {THREE.Object3D} gltfRoot — roads.glb 的 gltf.scene（Y-up，尚未套 rotX(90°)）
 * @param {Object} hub — 本场景共享的反射渲染调度（见 patchWaterReflectionForZUp）
 * @returns {THREE.Water[]} 创建的 Water 对象数组（已加入 gltfRoot，原 mesh 已移除）
 */
function replaceWaterWithShader(gltfRoot, hub) {
  const waterMeshes = []
  gltfRoot.traverse((o) => {
    if (o.isMesh && /^water/i.test(o.name || '') && !/^riverbed/i.test(o.name || '')) {
      waterMeshes.push(o)
    }
  })
  if (!waterMeshes.length) return []

  const normalsTex = waterNormalsTexture(256)
  const waterColor = NIGHT_MODE ? WATER_COLOR_NIGHT : 0x2f6f9f
  const sunColor = NIGHT_MODE ? SUN_KEY_COLOR_NIGHT : 0xffffff
  const waters = []

  waterMeshes.forEach((mesh) => {
    const water = new Water(mesh.geometry, {
      textureWidth: 256,
      textureHeight: 256,
      waterNormals: normalsTex,
      sunDirection: WATER_SUN_DIR.clone(),
      sunColor: sunColor,
      waterColor: waterColor,
      distortionScale: 3.7,
      alpha: WATER_OPACITY,
      size: 1.0,
      side: THREE.DoubleSide,
      fog: true,
    })
    // 继承原 mesh 的变换（在 GLB scene 里的位置 / 旋转 / 缩放）
    water.name = mesh.name
    water.position.copy(mesh.position)
    water.rotation.copy(mesh.rotation)
    water.scale.copy(mesh.scale)
    water.renderOrder = WATER_RENDER_ORDER

    // ── Z-up 着色器适配（见函数注释） ──
    water.material.fragmentShader = water.material.fragmentShader
      .replace('worldPosition.xz * size', 'worldPosition.xy * size')
      .replace('noise.xzy * vec3( 1.5, 1.0, 1.5 )', 'noise.xyz * vec3( 1.5, 1.0, 1.5 )')
      .replace('surfaceNormal.xz * (', 'surfaceNormal.xy * (')
      // 反射项压蓝（见 WATER_REFLECT_TINT）：albedo 的第二个 mix 参数整段是唯一锚点
      // 常数项同样要 g 压过 b：旧 vec3(0.04,0.09,0.17) 是 b−g(0.08) > g−r(0.05) 的紫底
      .replace(
        '( vec3( 0.1 ) + reflectionSample * 0.9 + reflectionSample * specularLight )',
        `( vec3( 0.022, 0.108, 0.168 ) + reflectionSample * ${WATER_REFLECT_TINT} * 0.9 + reflectionSample * specularLight )`,
      )
    // 锚点是第三方着色器里的字面量，three 升级会悄悄改掉 —— 打不上补丁画面只是"还是灰的"，
    // 没有任何报错，所以这里显式核对一次。
    if (water.material.fragmentShader.indexOf(WATER_REFLECT_TINT) < 0) {
      console.error('[environment] 水面反射蓝调补丁没打上：three 的 Water 着色器锚点变了，检查 albedo 那行')
    }
    water.material.needsUpdate = true

    // ── Z-up 反射适配：Water.onBeforeRender 硬编码 local normal (0,0,1)，
    //    但 GLB 几何的 local normal 是 (0,1,0)（Y-up → Z-up 转换后才是 +Z）。
    //    替换 onBeforeRender，仅改 normal.set 那行，其余逻辑原样保留。
    patchWaterReflectionForZUp(water, hub)

    // 替换：在父节点里用 Water 换掉原 mesh
    const parent = mesh.parent
    if (parent) {
      parent.remove(mesh)
      parent.add(water)
    }
    waters.push(water)
  })

  console.info(`[environment] Water 着色器水面 ${waters.length} 片（替换静态网格，含平面反射 + 涟漪动画）`)
  return waters
}

/**
 * 加载 trees.glb 并提取「实例源几何」：按对象名前缀归到树种，
 * 节点变换（含 Blender Z-up -> glTF Y-up 的转换旋转）烘进几何后，
 * 再 rotateX(90°) 转回项目 Z-up —— 得到「底面在原点、树干朝 +Z」的干净几何。
 * 松树三层锥冠合并为单几何（同材质），减少 draw call。
 * @returns {{broad?:{trunks:THREE.BufferGeometry[],canopies:THREE.BufferGeometry[],trunkMat,canopyMat}, pine?:..., flower?:..., bush?:...}}
 */
async function loadTreeParts(url) {
  const gltf = await new GLTFLoader().loadAsync(url)
  gltf.scene.updateMatrixWorld(true)
  const parts = {}
  gltf.scene.traverse((o) => {
    if (!o.isMesh) return
    const key = o.name.replace(/Mesh$/i, '') // 节点名 / 网格数据名都兜住
    let sp = null
    if (key.startsWith('TreeBroad')) sp = 'broad'
    else if (key.startsWith('TreePine')) sp = 'pine'
    else if (key.startsWith('TreeFlower')) sp = 'flower'
    else if (key.startsWith('TreeRound')) sp = 'round'
    else if (key.startsWith('Bush')) sp = 'bush'
    if (!sp) return
    const geo = o.geometry.clone().applyMatrix4(o.matrixWorld).rotateX(Math.PI / 2)
    const part = (parts[sp] = parts[sp] || { trunks: [], canopies: [], trunkMat: null, canopyMat: null })
    if (key.toLowerCase().includes('trunk')) {
      part.trunks.push(geo)
      part.trunkMat = part.trunkMat || o.material
    } else {
      part.canopies.push(geo)
      part.canopyMat = part.canopyMat || o.material
    }
  })
  Object.values(parts).forEach((p) => {
    if (p.canopies.length > 1) p.canopies = [mergeGeometries(p.canopies)]
    if (p.trunks.length > 1) p.trunks = [mergeGeometries(p.trunks)]
  })
  if (!Object.keys(parts).length) throw new Error('trees.glb 里没有可识别的树对象（Tree*/Bush*）')
  return parts
}

/** 把实例位姿/颜色灌进 InstancedMesh（GLB 树、清单树与程序化树共用） */
const _im = new THREE.Matrix4()
const _iq = new THREE.Quaternion()
const _ip = new THREE.Vector3()
const _is = new THREE.Vector3()
const _zAxis = new THREE.Vector3(0, 0, 1)
function fillInstances(mesh, items, tintFn) {
  items.forEach((it, i) => {
    _ip.set(it.e, it.n, it.z ?? 0.2)
    _is.setScalar(it.scale || 1)
    // 绕 Z（世界"上"轴）的朝向：清单里给的是弧度。原型冠是不规则团块，
    // 全部同朝向会一眼看出复制，旋转 + 缩放 + 颜色三样都要抖。
    if (it.rot) _iq.setFromAxisAngle(_zAxis, it.rot)
    else _iq.identity()
    _im.compose(_ip, _iq, _is)
    mesh.setMatrixAt(i, _im)
    if (tintFn) mesh.setColorAt(i, tintFn())
  })
  mesh.instanceMatrix.needsUpdate = true
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
  // 单位几何的包围球不覆盖实例的真实分布，按包围球剔除会整片消失，直接关掉
  mesh.frustumCulled = false
  return mesh
}

/**
 * 成林带清单并进主树位清单。树种索引**按名字换算**：两份清单各自带 species 表，
 * 谁先跑生成器都不会把 broad 铺成 flower。返回新对象，不改导入进来的 JSON 模块。
 */
function mergeBandSpots(plan, bands) {
  const spots = bands && Array.isArray(bands.spots) ? bands.spots : []
  if (!spots.length) return plan
  const from = bands.species || []
  const map = from.map((n) => (plan.species || []).indexOf(n))
  if (map.some((i) => i < 0) || spots.some((s) => !plan.species[map[s[3]]])) {
    console.warn('[environment] forest-band-spots.json 的树种表对不上，跳过成林带:', from)
    return plan
  }
  return { ...plan, spots: plan.spots.concat(spots.map((s) => [s[0], s[1], s[2], map[s[3]], s[4], s[5]])) }
}

/**
 * 按 Blender 树位清单铺实例：一个树种的冠/干各一个 InstancedMesh。
 * @param {Array<[number,number,number,number,number,number]>} spots [东, 北, 标高, 树种索引, 缩放, 旋转]
 */
function addPlannedTrees(group, parts, plan, tintFn, blocked) {
  const names = plan.species || []
  const bySpecies = new Map()
  let skipped = 0
  let pruned = 0
  /**
   * 冠幅的水平半径：用包围盒的宽/深半程，**不用包围球** —— 松塔是竖着长的，
   * 球的半径比树冠实际铺开的范围大好几倍，按它让位会把整片松林削没。
   */
  const crownR = {}
  const radiusOf = (sp) => {
    if (crownR[sp] === undefined) {
      const can = parts[sp] && parts[sp].canopies && parts[sp].canopies[0]
      const g = can && can.geometry
      if (g && !g.boundingBox) g.computeBoundingBox()
      const b = g && g.boundingBox
      crownR[sp] = b ? Math.max((b.max.x - b.min.x) / 2, (b.max.y - b.min.y) / 2) : 4
    }
    return crownR[sp]
  }
  plan.spots.forEach((s) => {
    const sp = names[s[3]]
    if (!sp || !parts[sp]) {
      skipped++
      return
    }
    // 让位按**冠幅**算，不按树干：这个机位是俯视，树干在楼里的那 1091 棵本来就被楼
    // 挡住，删掉画面几乎没变；用户说的"树和楼重叠"是树冠摊在屋顶上。
    // 0.75 = 允许叶尖轻轻擦到檐口，完全不相交会把楼边一圈绿地削成真空带。
    if (blocked && blocked(s[0], s[1], Math.max(2, radiusOf(sp) * (s[4] || 1) * 0.75))) {
      pruned++
      return
    }
    if (!bySpecies.has(sp)) bySpecies.set(sp, [])
    bySpecies.get(sp).push({ e: s[0], n: s[1], z: s[2], scale: s[4], rot: s[5] })
  })
  let meshes = 0
  let placed = 0
  bySpecies.forEach((items, sp) => {
    const p = parts[sp]
    placed += items.length
    if (p.canopies && p.canopies[0]) {
      group.add(fillInstances(new THREE.InstancedMesh(p.canopies[0], p.canopyMat, items.length), items, tintFn))
      meshes++
    }
    if (p.trunks && p.trunks[0]) {
      group.add(fillInstances(new THREE.InstancedMesh(p.trunks[0], p.trunkMat, items.length), items))
      meshes++
    }
  })
  return { placed, meshes, skipped, pruned }
}

/** envMap 只给这几类材质：背景幕墙 / 背景屋顶 / 水面。精模、绿地、道路一律不挂。 */
export const ENV_INTENSITY = { wall: 0.55, roof: 0.3, water: 1.0 }
function envIntensityFor(matName) {
  const n = matName || ''
  if (/^MContext_wall/.test(n)) return ENV_INTENSITY.wall
  if (/^MContext_roof/.test(n)) return ENV_INTENSITY.roof
  if (/water$/i.test(n) || /MRoad_water/.test(n)) return ENV_INTENSITY.water
  return 0
}

/**
 * 把 PMREM 环境贴图挂到指定材质上。
 * ⚠️ 不用 scene.environment：那等于给场景里**每一个** MeshStandardMaterial 加 IBL，
 * 23 栋精模的外观会被改掉（明确要求不动），绿地也会被洗亮成"发光睡莲叶"。
 * @returns {number} 挂上的材质数
 */
function applyEnvMap(root, tex) {
  if (!root || !tex) return 0
  const done = new Set()
  let n = 0
  root.traverse((o) => {
    const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
    ms.forEach((m) => {
      if (!m || done.has(m.uuid)) return
      done.add(m.uuid)
      const k = envIntensityFor(m.name)
      if (!k) return
      m.envMap = tex
      m.envMapIntensity = k
      m.needsUpdate = true // 换 envMap 要重编译着色器（一次性，初始化时付）
      n++
    })
  })
  return n
}

// ============ 贴花（接触阴影 / 光池）============

/** 软边径向渐变：白底 + alpha 衰减，一张图同时喂阴影和光池 */
let _decalTex = null
function decalTexture() {
  if (_decalTex) return _decalTex
  const S = 128
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = S
  const ctx = canvas.getContext('2d')
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2)
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(0.5, 'rgba(255,255,255,0.8)')
  g.addColorStop(0.82, 'rgba(255,255,255,0.22)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, S, S)
  _decalTex = new THREE.CanvasTexture(canvas)
  _decalTex.colorSpace = THREE.SRGBColorSpace
  return _decalTex
}

let _decalGeo = null
function decalGeometry() {
  // PlaneGeometry 天生就在 XY 平面、法向 +Z —— 在我们的 Z-up 世界里正好是水平片
  if (!_decalGeo) _decalGeo = new THREE.PlaneGeometry(1, 1)
  return _decalGeo
}

/**
 * 铺一批贴花。
 * @param {Array<[number,number,number,number]>} spots [东, 北, 标高, 半径]
 * @param {{color:number, opacity:number, drift?:number, order?:number}} opts
 *   drift = 沿 (-X,-Y)（西南）漂移的半径比例，主光 rimNE 从东北来，影子要落在背面
 */
function addDecals(group, spots, opts) {
  if (!Array.isArray(spots) || !spots.length) return null
  const mat = new THREE.MeshBasicMaterial({
    map: decalTexture(),
    color: opts.color,
    transparent: true,
    opacity: opts.opacity,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  })
  const mesh = new THREE.InstancedMesh(decalGeometry(), mat, spots.length)
  const m = new THREE.Matrix4()
  const q = new THREE.Quaternion()
  const p = new THREE.Vector3()
  const s = new THREE.Vector3()
  const drift = opts.drift || 0
  spots.forEach((sp, i) => {
    const r = sp[3]
    p.set(sp[0] - r * drift, sp[1] - r * drift, sp[2] + DECAL_LIFT)
    s.set(r * 2, r * 2, 1)
    m.compose(p, q, s)
    mesh.setMatrixAt(i, m)
  })
  mesh.instanceMatrix.needsUpdate = true
  // 贴花分布在整个园区，单位几何的包围球会把整批剔掉，直接关掉
  mesh.frustumCulled = false
  mesh.renderOrder = opts.order || 5 // 在楼/树之前画、但在地台与路面之后（靠 depthTest + polygonOffset 压住）
  group.add(mesh)
  return mesh
}

/** 按 decal-places.json 铺三类贴花，返回一句给日志用的统计 */
function addGroundDecals(group, blocked) {
  const d = DECAL_PLACES || {}
  const made = []
  // 树被楼吃掉的地方，接触阴影必须跟着一起掉：否则屋顶上会留一片"没有树的暗斑"。
  // 让位半径用阴影盘自己的半径（比树冠略大），保证"树被剔 = 影也被剔"。
  const shadows = blocked && d.treeShadow ? d.treeShadow.filter((sp) => !blocked(sp[0], sp[1], sp[3])) : d.treeShadow
  // 贴花的 renderOrder 必须**低于水面**：它们抬离地面 0.42m，比水面还高，
  // 靠队列顺序让水后画、把落在河道上的影子重新盖掉。
  if (addDecals(group, shadows, { color: 0x05070a, opacity: SHADOW_OPACITY, drift: SHADOW_TREE_DRIFT, order: WATER_RENDER_ORDER - 2 })) {
    made.push('树影 ' + shadows.length)
  }
  // 不传 additive：光池只是染色层，发光由灯头负责（见 LIGHT_POOL_OPACITY 的注释）
  if (NIGHT_MODE && addDecals(group, d.lightPool, { color: LIGHT_POOL_COLOR, opacity: LIGHT_POOL_OPACITY, order: WATER_RENDER_ORDER - 1 })) {
    made.push('光池 ' + d.lightPool.length)
  }
  return made
}

/**
 * 创建环境元素组（异步：优先加载 Blender GLB 资产，失败回退程序化几何）。
 * 直接 add 到 three 场景（世界 Z-up 坐标）。
 * @param {Array<{east:number, north:number, radius:number, name?:string}>} buildings
 *        有名称建筑的地面点（世界米制坐标）与占地半径，来自 sceneView 的标牌 userData
 * @returns {Promise<THREE.Group|null>} ENV_ENABLED 为 false 时返回 null；统计信息在 group.userData
 */
export async function createEnvironment({ buildings = [], blockers = [], envTexture = null } = {}) {
  if (!ENV_ENABLED) return null
  /**
   * 楼体禁区（世界米 [x0,x1,y0,y1]）—— 由 sceneView 用**渲染出来的**楼盒给。
   * ⚠️ 不能改用 art/building-layout.json 那套圆：那份布局是"模型校准"之前导出的，
   * 与现在画面里的楼平均差 162m（21 个分片各差各的），照它躲等于没躲。
   * pad 2m：树干离墙 2m 就不会读出"树从楼里长出来"，再贪远就要把成片林子削掉。
   */
  const blocked = (e, n, pad = 2) => {
    for (let i = 0; i < blockers.length; i++) {
      const b = blockers[i]
      if (e >= b[0] - pad && e <= b[1] + pad && n >= b[2] - pad && n <= b[3] + pad) return true
    }
    return false
  }

  const rng = mulberry32(RNG_SEED)
  const rand = (min, max) => min + (max - min) * rng()
  const pick = (arr) => arr[Math.floor(rng() * arr.length) % arr.length]
  /** 颜色小幅 HSL 抖动：同色系树冠也有深浅差异，避免「复印」感 */
  const vary = (hex) =>
    new THREE.Color(hex).offsetHSL(rand(-0.02, 0.02), rand(-0.12, 0.12), rand(-0.07, 0.07))
  /** 实例色微抖（GLB 树用近白抖动，不改变材质本色） */
  const tint = () => new THREE.Color(1, 1, 1).offsetHSL(rand(-0.03, 0.03), rand(-0.08, 0.08), rand(-0.06, 0.06))

  const group = new THREE.Group()
  group.name = 'environment'

  /**
   * 水面反射的每帧调度：token 由 sceneView 在每次出帧前 +1，done 记录哪一帧出过图；
   * target 是所有水面共用那张 RT。
   */
  const waterHub = {
    token: 0,
    done: -1,
    /** 本帧是否允许出反射图（见 begin） */
    reflect: true,
    /** 相机自上次出图后动过 */
    stale: false,
    target: null,
    /** 渲染反射那一遍时要藏掉的对象（见 hideForReflection） */
    reflectionSkip: [],
    _skipState: [],
    begin(moving) {
      this.token++
      // 反射那一遍等于**把整个场景再画一遍**：实测同机位同拖法，出反射 23fps、
      // 不出 43fps（tools/cdp-ab.js 配对量了两轮）。而它只是往一张 256² 的贴图里
      // 画楼影，所以只在"相机停过一次"之后重出：拖动途中沿用上一张，惯性收敛的
      // 那一帧补一张（出图后 stale 归位，面板自转那些帧不会再触发全场景渲染）。
      if (moving) this.stale = true
      this.reflect = this.done < 0 || (this.stale && !moving)
    },
    hideForReflection() {
      const list = this.reflectionSkip
      for (let i = 0; i < list.length; i++) {
        this._skipState[i] = list[i].visible
        list[i].visible = false
      }
    },
    showAfterReflection() {
      const list = this.reflectionSkip
      for (let i = 0; i < list.length; i++) list[i].visible = this._skipState[i]
    },
    dispose() {
      if (this.target) this.target.dispose()
      this.target = null
    },
  }
  let waterSurfaces = []

  // ---------- 资产加载（GLB 优先，失败回退程序化） ----------
  let treeParts = null
  let riverGltf = null
  let landscapeGltf = null
  let roadsGltf = null
  let contextGltf = null
  let furnitureGltf = null
  let hasLandscape = false
  let hasRoads = false
  let hasContext = false
  let treePlan = null
  try {
    if (GLB_URLS['trees.glb']) treeParts = await loadTreeParts(GLB_URLS['trees.glb'])
  } catch (e) {
    console.warn('[environment] trees.glb 加载失败，树回退程序化几何:', e)
  }
  // 清单只在拿到原型几何时生效：否则连"程序化兜底树"一起没了，画面会光秃秃。
  // 成林带位姿并进来一起铺：两份清单同格式同坐标系（Blender 世界坐标 = 场景坐标）。
  treePlan =
    treeParts && TREE_PLACES && Array.isArray(TREE_PLACES.spots) && TREE_PLACES.spots.length
      ? mergeBandSpots(TREE_PLACES, FOREST_BANDS)
      : null
  const hasTreePlan = !!treePlan
  try {
    if (ROADS_ENABLED && GLB_URLS['roads.glb']) {
      roadsGltf = await new GLTFLoader().loadAsync(GLB_URLS['roads.glb'])
    }
  } catch (e) {
    console.warn('[environment] roads.glb 加载失败，跳过道路铺装层:', e)
  }
  try {
    // river.glb 现在只作**兜底**：roads.glb 里已经带了一条按 OSM 真实水系（新开河 / 贴沙河）
    // 生成的 water 对象，两个一起加载会出现两条河。
    // 手作的 river.glb 那条"南侧大河"在 OSM 里根本没有对应水体，位置是错的。
    // ⚠️ 必须在 roads.glb 之后判断，否则 roadsGltf 永远是 null、兜底条件形同虚设。
    if (!roadsGltf && GLB_URLS['river.glb']) {
      riverGltf = await new GLTFLoader().loadAsync(GLB_URLS['river.glb'])
    }
  } catch (e) {
    console.warn('[environment] river.glb 加载失败，河道回退程序化几何:', e)
  }
  try {
    if (GLB_URLS['landscape.glb']) {
      landscapeGltf = await new GLTFLoader().loadAsync(GLB_URLS['landscape.glb'])
    }
  } catch (e) {
    console.warn('[environment] landscape.glb 加载失败，地面/成林回退程序化几何:', e)
  }
  try {
    if (GLB_URLS['furniture.glb']) {
      furnitureGltf = await new GLTFLoader().loadAsync(GLB_URLS['furniture.glb'])
    }
  } catch (e) {
    console.warn('[environment] furniture.glb 加载失败，跳过街道家具:', e)
  }
  try {
    if (CONTEXT_ENABLED && GLB_URLS['context.glb']) {
      contextGltf = await new GLTFLoader().loadAsync(GLB_URLS['context.glb'])
    }
  } catch (e) {
    console.warn('[environment] context.glb 加载失败，跳过周边城市背景:', e)
  }
  // ---------- 环境反射：只挂背景幕墙/屋顶 + 水面（材质是共享对象，挂一次即整批生效）----------
  if (envTexture) {
    const n =
      applyEnvMap(contextGltf && contextGltf.scene, envTexture) +
      applyEnvMap(roadsGltf && roadsGltf.scene, envTexture)
    console.info(`[environment] 环境反射（IBL）挂上材质 ${n} 个：背景幕墙/屋顶/水面；精模与绿地不挂`)
  }

  // ---------- 水面 ----------
  // roads.glb 里已经带了按 OSM 真实水系生成的 water 对象（对象名 water，网格 water_1/2/3）。
  // ⚠️ 有它的时候**程序化兜底河必须整条不画**：兜底河是"建筑南侧往南 150m + 正弦微弯"的
  // 手描几何，宽 55m、标高恒定 0.50m 完全不贴地形，和真水一起画就是在绿岛上横拉一条
  // 蓝色胶带。用户 2026-10-13 圈图问"右边这条蓝色的是什么"，找的三轮里两次都被我误判成
  // OSM 的河 —— 因为这条带的材质是 JS 里 new 出来的、**没有名字**，按材质名筛"水"筛不到它。
  const roadsHasWater = !!roadsGltf && (() => {
    let found = false
    roadsGltf.scene.traverse((o) => {
      if (!found && /^water/i.test(o.name || '')) found = true
    })
    return found
  })()
  const riverPts = riverGltf
    ? RIVER_CENTERLINE_GLB
    : RIVER_POINTS && RIVER_POINTS.length >= 2
      ? RIVER_POINTS
      : defaultRiverCenterline(buildings)
  // "有没有水"要算上 roads.glb 带的那条真水：以前只统计 riverGltf / 程序化兜底，
  // 于是日志里的"水面 1"其实是那条该死的胶带，关掉兜底后又变成"水面 0"，两头骗人。
  let hasWater = roadsHasWater
  if (riverGltf) {
    // GLB 河带：与城市模型同款的 Y-up -> Z-up 包装，位置精调走 RIVER_GLB_OFFSET
    const wrap = new THREE.Group()
    wrap.rotation.x = Math.PI / 2
    wrap.add(riverGltf.scene)
    wrap.position.set(RIVER_GLB_OFFSET.east, RIVER_GLB_OFFSET.north, WATER_Z)
    riverGltf.scene.traverse((o) => {
      if (!o.isMesh) return
      ;(Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        m.transparent = true
        m.opacity = WATER_OPACITY
        m.depthWrite = false // 与白模半透明立面共存，避免透明排序闪面
        if (NIGHT_MODE) m.color.multiplyScalar(0.5) // 夜景压暗水面，深蓝夜色（暖光下留一点反光）
      })
      o.renderOrder = WATER_RENDER_ORDER
    })
    group.add(wrap)
    hasWater = true
  } else if (!roadsHasWater && riverPts.length >= 2) {
    const geo = ribbonGeometry(riverPts, RIVER_WIDTH)
    if (geo) {
      const mesh = new THREE.Mesh(geo, waterMaterial())
      mesh.position.z = WATER_Z
      mesh.renderOrder = 1
      group.add(mesh)
      hasWater = true
    }
  }

  // ---------- Blender 景观资产（地形 + 成林 + 灌丛，已含建筑避让与河道避让） ----------
  if (landscapeGltf) {
    const wrap = new THREE.Group()
    wrap.rotation.x = Math.PI / 2 // Y-up -> Z-up，与城市模型同款
    wrap.add(landscapeGltf.scene)
    // 材质压暗（按 Blender 材质名）：夜景暖光 + ACES 会提亮，先压回自然色
    landscapeGltf.scene.traverse((o) => {
      if (!o.isMesh) return
      // 铺装盘 / 硬化带 / 草床是楼影的落点：不接阴影的话楼就是"飘"在灰地上
      o.receiveShadow = !!SHADOWS.enabled
      ;(Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        const n = m.name || ''
        if (n.includes('Pink')) m.color.multiplyScalar(0.7)
        else if (n.includes('Canopy') || n.includes('Bush')) m.color.multiplyScalar(0.6)
        else if (n.includes('Trunk')) m.color.multiplyScalar(0.8)
        else if (n.includes('Ground')) {
          // 顶点色本身是很深的绿（实测线性均值 0.056/0.138/0.055），夜景灯光下会糊成黑地；
          // 乘一层偏绿的亮系数比回 Blender 重画顶点色可控得多
          m.color.setRGB(GROUND_TINT.r, GROUND_TINT.g, GROUND_TINT.b)
          if (GROUND_EDGE_FADE) applyGroundEdgeFade(m)
        } else if (n === 'MPlazaStone' || n === 'MPathStone') {
          // 铺装盘/沿街硬化带代替地台做场景地面：边缘渐隐挂在这两个材质上。
          // ⚠️ 不要给 MLawnGrass/MLawnCurb 也挂上"图安心"：草地全部在 850m 内缘以里，
          // 渐隐对它是 no-op，但 transparent 会把草地推进透明队列改变渲染次序
          //（2026-10-21 中心圆盘被画成白饼的元凶嫌疑）。
          if (GROUND_EDGE_FADE) applyGroundEdgeFade(m)
        }
      })
    })
    group.add(wrap)
    hasLandscape = true
  }

  // ---------- 街道家具（长椅 / 垃圾桶 / 指示牌，art 手作低模，沿路缘合法点放置）----------
  if (furnitureGltf) {
    const wrap = new THREE.Group()
    wrap.name = 'furniture'
    wrap.rotation.x = Math.PI / 2 // Y-up -> Z-up，与 landscape / roads 同款
    wrap.add(furnitureGltf.scene)
    group.add(wrap)
  }

  // ---------- 道路 / 广场铺装（art/build_roads.py 生成，顶点已贴合 Terrain 起伏）----------
  if (roadsGltf) {
    // 在套 rotX(90°) 之前把静态水面网格换成 Water 着色器对象
    //（Water 的 onBeforeRender 会自动做平面反射渲染，time uniform 由 sceneView 的渲染循环推进）
    waterSurfaces = replaceWaterWithShader(roadsGltf.scene, waterHub)

    const wrap = new THREE.Group()
    wrap.name = 'roads'
    wrap.rotation.x = Math.PI / 2 // Y-up -> Z-up，与 landscape / river 同款
    wrap.add(roadsGltf.scene)
    wrap.position.set(ROADS_GLB_OFFSET.east, ROADS_GLB_OFFSET.north, 0)
    // 水面（MRoad_water 是 glTF BLEND，GLTFLoader 会顺手把 depthWrite 设成 false）
    // 必须画在透明地台之后，否则会被草地整个糊住 —— 见 WATER_RENDER_ORDER 的注释。
    //
    // 灯头：全场唯一"该发光"的东西。它只有 1m 见方，过阈值就是星芒；
    // 地面光池那种大面积元素不能拿来发光，见 LIGHT_POOL_OPACITY 的注释。
    // 真正起作用的是把 Blender 里那 6.0 的发射强度压下来（Bloom 比的是线性值，
    // 6.0 会把半条街糊成一片），toneMapped 这个标记开着后期时是空转的。
    wrap.traverse((o) => {
      if (!o.isMesh) return
      // Water 着色器对象（isWater）的材质是 ShaderMaterial，不走 MRoad_lamp_glow / transparent 那套
      if (o.isWater) return
      // 沥青/人行道/斑马线也要接楼影（Water 是 ShaderMaterial，吃不到 shadowmap，跳过）
      o.receiveShadow = !!SHADOWS.enabled
      const ms = Array.isArray(o.material) ? o.material : [o.material]
      ms.forEach((m) => {
        if (m && m.name === 'MRoad_lamp_glow') m.emissiveIntensity = LAMP_HDR_STRENGTH
      })
      if (ms.some((m) => m && m.transparent)) o.renderOrder = WATER_RENDER_ORDER
    })
    group.add(wrap)
    hasRoads = true
  }

  // ---------- 周边城市背景（OSM 真实轮廓挤出，见 art/build_context.py）----------
  if (contextGltf) {
    const wrap = new THREE.Group()
    wrap.name = 'context'
    wrap.rotation.x = Math.PI / 2 // Y-up -> Z-up，与其余 Blender 资产同款
    wrap.add(contextGltf.scene)
    wrap.position.set(CONTEXT_GLB_OFFSET.east, CONTEXT_GLB_OFFSET.north, 0)
    // 背景城的 wall/roof 是 alphaMode=BLEND（art/build_context.py 的 WALL_ALPHA）。
    // GLTFLoader 见到 BLEND 会顺手 depthWrite=false（three 官方为"透明互叠"做的折中，
    // 见 mrdoob/three.js#17706），但那对**一整块合并网格里的 1800 个盒子**是错的：
    // 没人写深度，每栋楼的正反面各叠一层（alpha 变 1−(1−a)²），而且挡不住后面的楼，
    // 背光那一面只剩一层暗 diffuse 糊在背景上 —— 观感就是"背光面全透明"。
    // 关掉它，一栋楼一个像素只留最先命中的那面，透明度才读成"玻璃体块"而不是"雾"。
    contextGltf.scene.traverse((o) => {
      if (!o.isMesh) return
      const mats = Array.isArray(o.material) ? o.material : [o.material]
      mats.forEach((m) => {
        if (m && m.transparent) m.depthWrite = true
      })
    })
    group.add(wrap)
    hasContext = true
  }
  PONDS.forEach((p) => {
    if (!p || !Array.isArray(p.points) || p.points.length < 3) return
    const shape = new THREE.Shape(p.points.map((q) => new THREE.Vector2(q[0], q[1])))
    const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), waterMaterial(p.color, p.opacity))
    mesh.position.z = WATER_Z
    mesh.renderOrder = 1
    group.add(mesh)
    hasWater = true
  })

  /** 树位是否落在水里（GLB 河取实测半宽+噪声余量，兜底河按 RIVER_WIDTH 估） */
  const waterHalf = riverGltf ? 42 : RIVER_WIDTH / 2 + 4
  const onWater = (e, n) => riverPts.length >= 2 && distToPolyline(e, n, riverPts) < waterHalf

  // ---------- 树位布点（先定点位+树种+缩放，GLB / 程序化两条路共用这份数据） ----------
  const treeSpots = [] // { e, n, species: 'broad'|'pine'|'flower', scale }
  const bushSpots = [] // { e, n, scale }
  const pickSpecies = () => {
    const r = rng()
    if (r < PINE_RATIO) return 'pine'
    if (r < PINE_RATIO + FLOWER_RATIO) return 'flower'
    return 'broad'
  }
  const pushSpot = (e, n) => {
    treeSpots.push({ e, n, species: pickSpecies(), scale: rand(TREE_SCALE[0], TREE_SCALE[1]) })
    // 灌丛：贴地小圆球，跟在部分树旁
    if (rng() < BUSH_RATIO) {
      const be = e + rand(-4, 4)
      const bn = n + rand(-4, 4)
      if (!inAnyBuilding(be, bn, buildings, 2) && !onWater(be, bn)) {
        bushSpots.push({ e: be, n: bn, scale: rand(0.8, 1.4), z: 0.2 })
      }
    }
  }
  if (AUTO_TREES && !hasLandscape && !hasTreePlan) {
    // 沿楼撒树（Blender 景观已加载时跳过：landscape 已避开楼体，避免重复叠树）
    const [cMin, cMax] = TREES_PER_BUILDING
    buildings.forEach((b) => {
      const r = Math.max(25, b.radius) // 占地半径过小（异形楼实测偏小）时兜底
      const count = cMin + Math.floor(rng() * (cMax - cMin + 1))
      for (let i = 0; i < count; i++) {
        const ang = rng() * Math.PI * 2
        const dist = r + TREE_MARGIN + rng() * 14
        const e = b.east + Math.cos(ang) * dist
        const n = b.north + Math.sin(ang) * dist
        if (inAnyBuilding(e, n, buildings, 3)) continue // 挪进了别的楼
        if (onWater(e, n)) continue
        pushSpot(e, n)
      }
    })
  }
  if (!hasTreePlan) {
    TREE_CLUSTERS.forEach((c) => {
      if (!c || !c.center) return
      for (let i = 0; i < (c.count || 20); i++) {
        const ang = rng() * Math.PI * 2
        const d = Math.sqrt(rng()) * (c.radius || 60) // 面积均匀：sqrt 保证圆内不聚心
        const e = c.center[0] + Math.cos(ang) * d
        const n = c.center[1] + Math.sin(ang) * d
        if (inAnyBuilding(e, n, buildings, 3)) continue
        if (onWater(e, n)) continue
        pushSpot(e, n)
      }
    })
  }

  // ---------- 草坪基面 + 园区成林（程序化兜底：Blender 景观已加载时跳过） ----------
  let groundR = 0
  if ((AUTO_GROUND || AUTO_FOREST) && !hasLandscape && buildings.length) {
    let se = 0
    let sn = 0
    buildings.forEach((b) => {
      se += b.east
      sn += b.north
    })
    const ce = se / buildings.length
    const cn = sn / buildings.length
    buildings.forEach((b) => {
      groundR = Math.max(groundR, Math.hypot(b.east - ce, b.north - cn))
    })
    groundR += GROUND_MARGIN
    if (AUTO_GROUND) {
      // 大片椭圆草地垫底：黑底消失、树有「落点」，水面也变成了草地上的河
      const ground = new THREE.Mesh(
        new THREE.CircleGeometry(groundR, 72),
        new THREE.MeshStandardMaterial({ color: GROUND_COLOR, roughness: 1 }),
      )
      ground.position.z = 0.12
      group.add(ground)
    }
    if (AUTO_FOREST) {
      // 成簇散布：随机「林团」中心 + 高斯式铺开 —— 树冠连成片，比均匀撒点更像真实林带
      const clumps = Math.max(8, Math.round(AUTO_FOREST_COUNT / 24))
      const perClump = Math.ceil(AUTO_FOREST_COUNT / clumps)
      for (let c = 0; c < clumps; c++) {
        const cAng = rng() * Math.PI * 2
        const cD = Math.sqrt(rng()) * groundR * 0.92
        const cE = ce + Math.cos(cAng) * cD
        const cN = cn + Math.sin(cAng) * cD
        const spread = 40 + rng() * 80
        for (let i = 0; i < perClump; i++) {
          const e = cE + rand(-spread, spread)
          const n = cN + rand(-spread, spread)
          if (Math.hypot(e - ce, n - cn) > groundR) continue
          if (inAnyBuilding(e, n, buildings, 4)) continue
          if (onWater(e, n)) continue
          pushSpot(e, n)
        }
      }
    }
  }

  // ---------- 树实例化 ----------
  let planned = null
  if (hasTreePlan) {
    // Blender 清单：位置/标高/树种/缩放/朝向全部来自 art/build_roads.py
    planned = addPlannedTrees(group, treeParts, treePlan, tint, blocked)
    console.info(
      `[environment] 树位清单铺实例：${planned.placed} 个 / ${treePlan.spots.length} 条` +
        `（含成林带 ${(FOREST_BANDS.spots || []).length} 条，` +
        `${planned.meshes} 个 InstancedMesh，跳过 ${planned.skipped}，` +
        `落在渲染出来的楼体里剔除 ${planned.pruned}）`,
    )
  } else if (treeParts) {
    // GLB 路径：按树种分组，冠/干各一个 InstancedMesh（每种树 2 个 draw call）
    const bySpecies = { broad: [], pine: [], flower: [], round: [] }
    treeSpots.forEach((s) => bySpecies[s.species] && bySpecies[s.species].push(s))
    Object.entries(treeParts).forEach(([sp, p]) => {
      const items = bySpecies[sp]
      if (!items || !items.length) return
      if (p.canopies[0]) {
        group.add(fillInstances(new THREE.InstancedMesh(p.canopies[0], p.canopyMat, items.length), items, tint))
      }
      if (p.trunks[0]) {
        group.add(fillInstances(new THREE.InstancedMesh(p.trunks[0], p.trunkMat, items.length), items, tint))
      }
    })
    if (treeParts.bush && treeParts.bush.canopies[0] && bushSpots.length) {
      group.add(
        fillInstances(
          new THREE.InstancedMesh(treeParts.bush.canopies[0], treeParts.bush.canopyMat, bushSpots.length),
          bushSpots,
          tint,
        ),
      )
    }
  } else {
    // 程序化兜底：单位几何 + 每棵随机尺寸/颜色（与旧方案一致）
    const trunks = []
    const blobs = []
    const pines = []
    treeSpots.forEach((s) => {
      const h = rand(TREE_HEIGHT[0], TREE_HEIGHT[1]) * s.scale
      const r = rand(CANOPY_RADIUS[0], CANOPY_RADIUS[1]) * s.scale
      const trunkH = h * 0.42
      const trunkR = 0.35 + h * 0.012
      trunks.push({ e: s.e, n: s.n, z: 0.2, sx: trunkR, sy: trunkR, sz: trunkH })
      if (s.species === 'pine') {
        pines.push({ e: s.e, n: s.n, z: trunkH * 0.45, sx: r * 0.95, sy: r * 0.95, sz: h * 0.75, color: vary(0x2f6b46) })
      } else {
        blobs.push({
          e: s.e,
          n: s.n,
          z: trunkH + r * 0.55,
          sx: r,
          sy: r,
          sz: r * 0.9,
          color: vary(s.species === 'flower' ? pick(PINKS) : pick(GREENS)),
        })
      }
    })
    const trunkMat = new THREE.MeshStandardMaterial({ color: 0x6b4f3a, roughness: 1 })
    const leafMat = new THREE.MeshStandardMaterial({ roughness: 0.95 })
    if (trunks.length) group.add(fillInstances(new THREE.InstancedMesh(unitTrunkGeometry(), trunkMat, trunks.length), trunks))
    if (blobs.length) group.add(fillInstances(new THREE.InstancedMesh(unitBlobGeometry(), leafMat, blobs.length), blobs, () => vary(pick(GREENS.concat(PINKS)))))
    if (pines.length) group.add(fillInstances(new THREE.InstancedMesh(unitPineGeometry(), leafMat, pines.length), pines))
  }
  if (!treeParts && bushSpots.length) {
    // 兜底灌丛（GLB 树的灌丛走 treeParts.bush，上面已建）
    group.add(
      fillInstances(
        new THREE.InstancedMesh(unitBlobGeometry(), new THREE.MeshStandardMaterial({ roughness: 0.95 }), bushSpots.length),
        bushSpots.map((b) => ({ ...b, sx: b.scale * 1.8, sy: b.scale * 1.8, sz: b.scale * 1.1 })),
        () => vary(pick(GREENS.concat(PINKS))),
      ),
    )
  }

  // ---------- 贴花：接触阴影 + 灯光池（接地感，参考图最依赖这一层）----------
  let decalMade = []
  if (GROUND_DECALS) decalMade = addGroundDecals(group, blocked)

  // ---------- 草坪（始终程序化：贴地圆片足够） ----------
  let lawnCount = 0
  const lawnSpecs = LAWNS.slice()
  if (AUTO_LAWNS && !hasLandscape && buildings.length >= 3) {
    // 建筑群几何中心铺一片草坪：圆心被楼压住就放弃，宁缺毋滥
    let se = 0
    let sn = 0
    buildings.forEach((b) => {
      se += b.east
      sn += b.north
    })
    const ce = se / buildings.length
    const cn = sn / buildings.length
    if (!inAnyBuilding(ce, cn, buildings, 10)) {
      let maxD = 0
      buildings.forEach((b) => {
        maxD = Math.max(maxD, Math.hypot(b.east - ce, b.north - cn))
      })
      lawnSpecs.push({ center: [ce, cn], radius: Math.min(90, Math.max(35, maxD * 0.3)) })
    }
  }
  lawnSpecs.forEach((l) => {
    if (!l || !l.center) return
    if (inAnyBuilding(l.center[0], l.center[1], buildings, 4)) return
    const mesh = new THREE.Mesh(
      new THREE.CircleGeometry(l.radius || 50, 48),
      new THREE.MeshStandardMaterial({
        color: l.color ?? 0x5d9c6a,
        transparent: true,
        opacity: 0.5,
        roughness: 1,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    )
    mesh.position.set(l.center[0], l.center[1], 0.35)
    group.add(mesh)
    lawnCount++
  })

  // 反射贴图只有 256²：树/灌木/贴花/草片这些 InstancedMesh（环境层九成的三角形都在
  // 它们身上）在那一遍里一个像素都占不到，让它们别跟着整场景再画一遍
  group.traverse((o) => {
    if (o.isInstancedMesh) waterHub.reflectionSkip.push(o)
  })

  group.userData = {
    treeCount: hasTreePlan && planned ? planned.placed : treeSpots.length,
    fromPlan: hasTreePlan,
    planCount: treePlan ? treePlan.spots.length : 0,
    decals: decalMade,
    bushCount: bushSpots.length,
    lawnCount,
    hasWater,
    hasLandscape,
    hasRoads,
    hasContext,
    source: treeParts || riverGltf || landscapeGltf || roadsGltf || contextGltf ? 'glb' : 'procedural',
    // ⚠️ 这几个必须写在这一次赋值里：group.userData 到这里是**整块换掉**的，
    // 之前 waterSurfaces 是在 roads 分支里单独挂的，于是被抹掉 —— sceneView 拿到空数组，
    // Water 的 time uniform 没人推进，河面涟漪从一开始就没在动。
    waterSurfaces,
    waterHub,
  }
  return group
}
